// Interfaz para los valores de tipo de documento de receptor
export interface TipoDocumentoReceptorValue {
  id: number;
  catalog_id: number;
  code: string;
  value: string;
  created_at: string;
  updated_at: string;
}

// Interfaz para la respuesta completa del catálogo CAT-022
export interface TipoDocumentoReceptorCatalog {
  id: number;
  catalog: "CAT-022";
  description: "Tipo de Documento de Receptor";
  created_at: string;
  updated_at: string;
  values: TipoDocumentoReceptorValue[];
}

// Tipo para la respuesta del endpoint GET /catalog/CAT-022
export type CAT022Response = TipoDocumentoReceptorCatalog;

// Tipos específicos para errores de CAT-022
export interface CAT022Error {
  message: string;
  status?: number;
}

// Enum para los códigos de tipo de documento más comunes
export enum TipoDocumentoCode {
  DUI = "13",
  CARNET_RESIDENTE = "02", 
  PASAPORTE = "03",
  NIT = "36",
  OTRO = "37"
}

// Tipo para validación de códigos
export type ValidTipoDocumentoCode = "13" | "02" | "03" | "36" | "37";

// Datos completos del catálogo CAT-022 - Tipo de Documento de Receptor
export const CAT022_DATA: TipoDocumentoReceptorValue[] = [
  {
    id: 1,
    catalog_id: 22,
    code: "13",
    value: "DUI",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z"
  },
  {
    id: 2,
    catalog_id: 22,
    code: "02",
    value: "Carnet de Residente",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z"
  },
  {
    id: 3,
    catalog_id: 22,
    code: "03",
    value: "Pasaporte",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z"
  },
  {
    id: 4,
    catalog_id: 22,
    code: "36",
    value: "NIT",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z"
  },
  {
    id: 5,
    catalog_id: 22,
    code: "37",
    value: "Otro",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z"
  }
];

// Helper: Buscar tipo de documento por código
export function getTipoDocumentoByCode(code: string): TipoDocumentoReceptorValue | undefined {
  return CAT022_DATA.find(item => item.code === code);
}

// Helper: Buscar tipo de documento por valor
export function getTipoDocumentoByValue(value: string): TipoDocumentoReceptorValue | undefined {
  return CAT022_DATA.find(item => item.value.toLowerCase() === value.toLowerCase());
}

// Helper: Validar si un código de tipo de documento es válido
export function isValidTipoDocumentoCode(code: string): code is ValidTipoDocumentoCode {
  return CAT022_DATA.some(item => item.code === code);
}

// Helper: Obtener todos los tipos de documento como options para select
export function getTipoDocumentoOptions(): Array<{ value: string; label: string }> {
  return CAT022_DATA.map(item => ({
    value: item.code,
    label: `${item.value} (${item.code})`
  }));
}

// Helper: Detectar automáticamente el tipo de documento basado en el número
export function detectTipoDocumento(numeroDocumento: string): TipoDocumentoCode {
  // Limpiar el número de documento
  const cleanDoc = numeroDocumento.replace(/[-\s]/g, '');
  
  // NIT: 14 dígitos
  if (/^\d{14}$/.test(cleanDoc)) {
    return TipoDocumentoCode.NIT;
  }
  
  // DUI: 8-9 dígitos con guiones (formato XXXXXXXX-X)
  if (/^\d{8}-?\d$/.test(numeroDocumento) || /^\d{8,9}$/.test(cleanDoc)) {
    return TipoDocumentoCode.DUI;
  }
  
  // Pasaporte: letras y números
  if (/^[A-Z0-9]{6,9}$/i.test(cleanDoc)) {
    return TipoDocumentoCode.PASAPORTE;
  }
  
  // Default: DUI (para mantener compatibilidad)
  return TipoDocumentoCode.DUI;
}

// Helper: Formatear número de documento según el tipo
export function formatNumeroDocumento(numeroDocumento: string, tipoDocumento: TipoDocumentoCode): string {
  const cleanDoc = numeroDocumento.replace(/[-\s]/g, '');
  
  switch (tipoDocumento) {
    case TipoDocumentoCode.DUI:
      // Formato DUI: XXXXXXXX-X
      if (cleanDoc.length === 9) {
        return `${cleanDoc.slice(0, 8)}-${cleanDoc.slice(8)}`;
      }
      return cleanDoc;
      
    case TipoDocumentoCode.NIT:
      // Formato NIT: XXXXXXXXXXXXXX (14 dígitos sin guiones)
      return cleanDoc;
      
    case TipoDocumentoCode.PASAPORTE:
    case TipoDocumentoCode.CARNET_RESIDENTE:
    case TipoDocumentoCode.OTRO:
    default:
      return numeroDocumento.toUpperCase();
  }
}

// Helper: Validar formato de número de documento
export function validateNumeroDocumento(numeroDocumento: string, tipoDocumento: TipoDocumentoCode): boolean {
  const cleanDoc = numeroDocumento.replace(/[-\s]/g, '');
  
  switch (tipoDocumento) {
    case TipoDocumentoCode.DUI:
      // DUI: exactamente 9 dígitos
      return /^\d{9}$/.test(cleanDoc);
      
    case TipoDocumentoCode.NIT:
      // NIT: exactamente 14 dígitos
      return /^\d{14}$/.test(cleanDoc);
      
    case TipoDocumentoCode.PASAPORTE:
      // Pasaporte: 6-9 caracteres alfanuméricos
      return /^[A-Z0-9]{6,9}$/i.test(cleanDoc);
      
    case TipoDocumentoCode.CARNET_RESIDENTE:
      // Carnet de residente: hasta 20 caracteres alfanuméricos
      return /^[A-Z0-9]{1,20}$/i.test(cleanDoc);
      
    case TipoDocumentoCode.OTRO:
      // Otro: cualquier formato, máximo 20 caracteres
      return numeroDocumento.length <= 20;
      
    default:
      return false;
  }
}

// Helper: Obtener mensaje de error de validación
export function getValidationErrorMessage(numeroDocumento: string, tipoDocumento: TipoDocumentoCode): string | null {
  if (validateNumeroDocumento(numeroDocumento, tipoDocumento)) {
    return null;
  }
  
  switch (tipoDocumento) {
    case TipoDocumentoCode.DUI:
      return "El DUI debe tener exactamente 9 dígitos (formato: XXXXXXXX-X)";
      
    case TipoDocumentoCode.NIT:
      return "El NIT debe tener exactamente 14 dígitos";
      
    case TipoDocumentoCode.PASAPORTE:
      return "El pasaporte debe tener entre 6 y 9 caracteres alfanuméricos";
      
    case TipoDocumentoCode.CARNET_RESIDENTE:
      return "El carnet de residente debe tener hasta 20 caracteres alfanuméricos";
      
    case TipoDocumentoCode.OTRO:
      return "El documento debe tener máximo 20 caracteres";
      
    default:
      return "Formato de documento inválido";
  }
}

// Helper: Convertir TipoDocumentoCode a tipo válido para interfaz Receptor
export function toReceptorTipoDocumento(tipoDocumento: TipoDocumentoCode): "13" | "36" | "37" | "38" | "39" {
  switch (tipoDocumento) {
    case TipoDocumentoCode.DUI:
      return "13";
    case TipoDocumentoCode.CARNET_RESIDENTE:
      return "37"; // Mapear carnet de residente a 37
    case TipoDocumentoCode.PASAPORTE:
      return "38"; // Mapear pasaporte a 38
    case TipoDocumentoCode.NIT:
      return "36";
    case TipoDocumentoCode.OTRO:
      return "39"; // Mapear otro a 39
    default:
      return "13"; // Default DUI
  }
}

// Helper: Crear objeto receptor con validación automática
export interface ReceptorData {
  nombre: string;
  numeroDocumento: string;
  tipoDocumento?: TipoDocumentoCode;
  correoElectronico?: string;
  telefono?: string;
}

export function createValidatedReceptor(data: ReceptorData) {
  // Detectar automáticamente el tipo de documento si no se proporciona
  const tipoDocumento = data.tipoDocumento || detectTipoDocumento(data.numeroDocumento);
  
  // Formatear el número de documento
  const numeroDocumentoFormateado = formatNumeroDocumento(data.numeroDocumento, tipoDocumento);
  
  // Validar el formato
  const validationError = getValidationErrorMessage(numeroDocumentoFormateado, tipoDocumento);
  if (validationError) {
    throw new Error(`Error de validación: ${validationError}`);
  }
  
  return {
    nombre: data.nombre,
    tipoDocumento: tipoDocumento,
    numDocumento: numeroDocumentoFormateado,
    correoElectronico: data.correoElectronico || null,
    telefono: data.telefono || null,
    nrc: null // Siempre null según las correcciones aplicadas
  };
}

// Helper: Crear objeto receptor con validación automática y tipos correctos para DTE
export interface ReceptorDataForDTE {
  nombre: string;
  numeroDocumento: string;
  tipoDocumento?: TipoDocumentoCode;
  correoElectronico?: string;
  telefono?: string;
}

export function createValidatedReceptorForDTE(data: ReceptorDataForDTE) {
  // Detectar automáticamente el tipo de documento si no se proporciona
  const tipoDocumento = data.tipoDocumento || detectTipoDocumento(data.numeroDocumento);
  
  // Formatear el número de documento
  const numeroDocumentoFormateado = formatNumeroDocumento(data.numeroDocumento, tipoDocumento);
  
  // Validar el formato
  const validationError = getValidationErrorMessage(numeroDocumentoFormateado, tipoDocumento);
  if (validationError) {
    throw new Error(`Error de validación: ${validationError}`);
  }
  
  return {
    nombre: data.nombre,
    tipoDocumento: toReceptorTipoDocumento(tipoDocumento),
    numDocumento: numeroDocumentoFormateado,
    correoElectronico: data.correoElectronico || null,
    telefono: data.telefono || null,
    nrc: null // Siempre null según las correcciones aplicadas
  };
}